import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";
import {
  applyEntityFilters,
  createEntityImageSignedUrl,
  createEntityCard as createEntityCardClient,
  createEntityRelationship as createEntityRelationshipClient,
  deleteEntityCard as deleteEntityCardClient,
  deleteEntityRelationship as deleteEntityRelationshipClient,
  formatEntityErrorMessage,
  getSelectedCardHiddenByFilters,
  loadEntityWorkspace,
  upsertEntityCard,
  updateEntityCard as updateEntityCardClient,
  updateEntityRelationship as updateEntityRelationshipClient,
} from "@/features/entities";
import type {
  CreateEntityCardInput,
  CreateEntityRelationshipInput,
  EntityCardTargetOption,
  EntityCardType,
  EntityCardView,
  EntityLibraryFilters,
  EntityLimitPrompt,
  EntityLoreOption,
  EntityRelationshipTargetOption,
  EntityRelationshipView,
  ProjectEntityCapacitySummary,
  UpdateEntityRelationshipInput,
} from "@/features/entities";
import { EntityActionError } from "@/features/entities";
import { subscriptionStore } from "@/stores/subscriptionStore";

export interface EntityWorkspaceDependencies {
  createEntityCard: typeof createEntityCardClient;
  createEntityRelationship: typeof createEntityRelationshipClient;
  deleteEntityCard: typeof deleteEntityCardClient;
  deleteEntityRelationship: typeof deleteEntityRelationshipClient;
  loadEntityWorkspace: typeof loadEntityWorkspace;
  updateEntityCard: typeof updateEntityCardClient;
  updateEntityRelationship: typeof updateEntityRelationshipClient;
}

export interface EntityWorkspaceNotice {
  kind: "error" | "success";
  message: string;
}

export interface EntityDeleteDialogState {
  open: boolean;
  submitting: boolean;
  target: EntityCardView | null;
}

export interface EntityWorkspaceStoreState {
  activeCardId: string | null;
  applyGeneratedImage: (
    cardId: string,
    imagePath: string,
    creditsRemaining?: number | null,
  ) => Promise<void>;
  cancelCreateCard: () => void;
  capacity: ProjectEntityCapacitySummary | null;
  cards: EntityCardView[];
  characterTargetOptions: EntityCardTargetOption[];
  clearFilters: () => void;
  clearLimitPrompt: () => void;
  clearNotice: () => void;
  closeDeleteDialog: () => void;
  createRelationship: (
    input: Omit<CreateEntityRelationshipInput, "projectId" | "sourceEntityCardId">,
  ) => Promise<EntityRelationshipView>;
  deleteCard: () => Promise<void>;
  deleteDialog: EntityDeleteDialogState;
  deleteRelationship: (relationshipId: string) => Promise<{ deletedRelationshipId: string }>;
  draftType: EntityCardType | null;
  filters: EntityLibraryFilters;
  hydrateWorkspace: (projectId: string, cardId?: string | null) => Promise<void>;
  isRecoveringMedia: boolean;
  limitPrompt: EntityLimitPrompt | null;
  markMediaRecoveryRequired: () => void;
  mediaRecoveryRequired: boolean;
  notice: EntityWorkspaceNotice | null;
  openCard: (cardId: string) => void;
  openDeleteDialog: (cardId: string) => void;
  projectId: string | null;
  raceSpeciesOptions: EntityLoreOption[];
  refreshMediaUrls: () => Promise<void>;
  relationshipTargetOptions: EntityRelationshipTargetOption[];
  saveCard: (input: Omit<CreateEntityCardInput, "projectId">) => Promise<EntityCardView>;
  saveRelationship: (
    relationshipId: string,
    input: Omit<UpdateEntityRelationshipInput, "projectId" | "relationshipId" | "sourceEntityCardId">,
  ) => Promise<EntityRelationshipView>;
  setSearchText: (searchText: string) => void;
  setTypeFilter: (typeFilter: EntityLibraryFilters["typeFilter"]) => void;
  startCreateCard: (type: EntityCardType) => void;
  status: "idle" | "loading" | "ready" | "error";
  unavailableCardId: string | null;
}

const defaultDependencies: EntityWorkspaceDependencies = {
  createEntityCard: createEntityCardClient,
  createEntityRelationship: createEntityRelationshipClient,
  deleteEntityCard: deleteEntityCardClient,
  deleteEntityRelationship: deleteEntityRelationshipClient,
  loadEntityWorkspace,
  updateEntityCard: updateEntityCardClient,
  updateEntityRelationship: updateEntityRelationshipClient,
};

function getLimitPrompt(error: unknown) {
  return error instanceof EntityActionError ? error.limitPrompt ?? null : null;
}

function resolveNextActiveCardId(
  cards: EntityCardView[],
  requestedCardId: string | null,
  previousActiveCardId: string | null,
) {
  if (requestedCardId && cards.some((card) => card.id === requestedCardId)) {
    return requestedCardId;
  }

  if (previousActiveCardId && cards.some((card) => card.id === previousActiveCardId)) {
    return previousActiveCardId;
  }

  return cards[0]?.id ?? null;
}

export function createEntityWorkspaceStore(
  deps: EntityWorkspaceDependencies = defaultDependencies,
) {
  return createStore<EntityWorkspaceStoreState>((set, get) => ({
    activeCardId: null,
    capacity: null,
    cards: [],
    characterTargetOptions: [],
    deleteDialog: {
      open: false,
      submitting: false,
      target: null,
    },
    draftType: null,
    filters: {
      searchText: "",
      typeFilter: "all",
    },
    isRecoveringMedia: false,
    limitPrompt: null,
    mediaRecoveryRequired: false,
    notice: null,
    projectId: null,
    raceSpeciesOptions: [],
    relationshipTargetOptions: [],
    status: "idle",
    unavailableCardId: null,

    applyGeneratedImage: async (cardId, imagePath, creditsRemaining = null) => {
      const imageUrl = await createEntityImageSignedUrl(imagePath).catch(() => null);

      if (typeof creditsRemaining === "number") {
        subscriptionStore.getState().setCreditBalance(creditsRemaining);
      }

      set((state) => {
        const card = state.cards.find((candidate) => candidate.id === cardId);
        if (!card) {
          return {};
        }

        return {
          cards: upsertEntityCard(state.cards, {
            ...card,
            hasImage: true,
            imagePath,
            imageUrl,
          }),
          mediaRecoveryRequired: imageUrl ? state.mediaRecoveryRequired : true,
        };
      });
    },

    hydrateWorkspace: async (projectId, cardId = null) => {
      set({
        activeCardId: null,
        cards: [],
        characterTargetOptions: [],
        deleteDialog: {
          open: false,
          submitting: false,
          target: null,
        },
        draftType: null,
        isRecoveringMedia: false,
        limitPrompt: null,
        mediaRecoveryRequired: false,
        notice: null,
        projectId,
        raceSpeciesOptions: [],
        relationshipTargetOptions: [],
        status: "loading",
        unavailableCardId: null,
      });

      try {
        const snapshot = await deps.loadEntityWorkspace(projectId);
        const activeCardId = cardId
          ? (snapshot.cards.some((card) => card.id === cardId) ? cardId : null)
          : resolveNextActiveCardId(snapshot.cards, null, null);

        set({
          activeCardId,
          capacity: snapshot.capacity,
          cards: snapshot.cards,
          characterTargetOptions: snapshot.characterTargetOptions,
          isRecoveringMedia: false,
          mediaRecoveryRequired: false,
          projectId,
          raceSpeciesOptions: snapshot.raceSpeciesOptions,
          relationshipTargetOptions: snapshot.relationshipTargetOptions,
          status: "ready",
          unavailableCardId: cardId && !activeCardId ? cardId : null,
        });
      } catch (error) {
        set({
          notice: {
            kind: "error",
            message: formatEntityErrorMessage(error),
          },
          projectId,
          status: "error",
        });
      }
    },

    startCreateCard: (type) => {
      set({
        activeCardId: null,
        draftType: type,
        limitPrompt: null,
        mediaRecoveryRequired: false,
        unavailableCardId: null,
      });
    },

    cancelCreateCard: () => {
      set((state) => ({
        activeCardId: state.cards[0]?.id ?? null,
        draftType: null,
        unavailableCardId: null,
      }));
    },

    openCard: (cardId) => {
      set({
        activeCardId: cardId,
        draftType: null,
        unavailableCardId: null,
      });
    },

    setSearchText: (searchText) => {
      set((state) => ({
        filters: {
          ...state.filters,
          searchText,
        },
      }));
    },

    setTypeFilter: (typeFilter) => {
      set((state) => ({
        filters: {
          ...state.filters,
          typeFilter,
        },
      }));
    },

    clearFilters: () => {
      set({
        filters: {
          searchText: "",
          typeFilter: "all",
        },
      });
    },

    saveCard: async (input) => {
      const projectId = get().projectId;
      if (!projectId) {
        throw new EntityActionError("not-found", "That project is unavailable.");
      }

      const activeCardId = get().activeCardId;
      const draftType = get().draftType;

      try {
        const record = draftType
          ? await deps.createEntityCard(projectId, input)
          : activeCardId
            ? await deps.updateEntityCard(activeCardId, input)
            : null;

        if (!record) {
          throw new EntityActionError("not-found", "That Entity Card is unavailable.");
        }

        const snapshot = await deps.loadEntityWorkspace(projectId);
        const nextCard = snapshot.cards.find((card) => card.id === record.id) ?? null;

        set({
          activeCardId: nextCard?.id ?? null,
          capacity: snapshot.capacity,
          cards: snapshot.cards,
          characterTargetOptions: snapshot.characterTargetOptions,
          deleteDialog: {
            open: false,
            submitting: false,
            target: null,
          },
          draftType: null,
          isRecoveringMedia: false,
          limitPrompt: null,
          mediaRecoveryRequired: false,
          notice: null,
          raceSpeciesOptions: snapshot.raceSpeciesOptions,
          relationshipTargetOptions: snapshot.relationshipTargetOptions,
          status: "ready",
          unavailableCardId: null,
        });

        if (!nextCard) {
          throw new EntityActionError("not-found", "The saved Entity Card is unavailable.");
        }

        return nextCard;
      } catch (error) {
        set({
          limitPrompt: getLimitPrompt(error),
          notice: {
            kind: "error",
            message: formatEntityErrorMessage(error),
          },
        });
        throw error;
      }
    },

    openDeleteDialog: (cardId) => {
      const target = get().cards.find((card) => card.id === cardId) ?? null;

      set({
        deleteDialog: {
          open: Boolean(target),
          submitting: false,
          target,
        },
      });
    },

    closeDeleteDialog: () => {
      set({
        deleteDialog: {
          open: false,
          submitting: false,
          target: null,
        },
      });
    },

    deleteCard: async () => {
      const projectId = get().projectId;
      const target = get().deleteDialog.target;

      if (!projectId || !target) {
        throw new EntityActionError("not-found", "That Entity Card is unavailable.");
      }

      set((state) => ({
        deleteDialog: {
          ...state.deleteDialog,
          submitting: true,
        },
      }));

      try {
        await deps.deleteEntityCard(target.id);
        const snapshot = await deps.loadEntityWorkspace(projectId);
        const nextActiveCardId = resolveNextActiveCardId(
          snapshot.cards,
          null,
          snapshot.cards[0]?.id ?? null,
        );

        set({
          activeCardId: nextActiveCardId,
          capacity: snapshot.capacity,
          cards: snapshot.cards,
          characterTargetOptions: snapshot.characterTargetOptions,
          deleteDialog: {
            open: false,
            submitting: false,
            target: null,
          },
          draftType: null,
          isRecoveringMedia: false,
          mediaRecoveryRequired: false,
          notice: null,
          raceSpeciesOptions: snapshot.raceSpeciesOptions,
          relationshipTargetOptions: snapshot.relationshipTargetOptions,
          status: "ready",
          unavailableCardId: null,
        });
      } catch (error) {
        set((state) => ({
          deleteDialog: {
            ...state.deleteDialog,
            submitting: false,
          },
          notice: {
            kind: "error",
            message: formatEntityErrorMessage(error),
          },
        }));
        throw error;
      }
    },

    createRelationship: async (input) => {
      const projectId = get().projectId;
      const activeCardId = get().activeCardId;

      if (!projectId || !activeCardId) {
        throw new EntityActionError("not-found", "That Entity Card is unavailable.");
      }

      try {
        const relationship = await deps.createEntityRelationship(projectId, activeCardId, input);
        const snapshot = await deps.loadEntityWorkspace(projectId);
        const activeCard = snapshot.cards.find((card) => card.id === activeCardId);
        const nextRelationship = activeCard?.relationships.find((entry) => entry.id === relationship.id);

        set({
          capacity: snapshot.capacity,
          cards: snapshot.cards,
          characterTargetOptions: snapshot.characterTargetOptions,
          isRecoveringMedia: false,
          mediaRecoveryRequired: false,
          notice: null,
          raceSpeciesOptions: snapshot.raceSpeciesOptions,
          relationshipTargetOptions: snapshot.relationshipTargetOptions,
        });

        if (!nextRelationship) {
          throw new EntityActionError("not-found", "The saved relationship is unavailable.");
        }

        return nextRelationship;
      } catch (error) {
        set({
          notice: {
            kind: "error",
            message: formatEntityErrorMessage(error),
          },
        });
        throw error;
      }
    },

    saveRelationship: async (relationshipId, input) => {
      const projectId = get().projectId;
      const activeCardId = get().activeCardId;

      if (!projectId || !activeCardId) {
        throw new EntityActionError("not-found", "That Entity Card is unavailable.");
      }

      try {
        const relationship = await deps.updateEntityRelationship(relationshipId, input);
        const snapshot = await deps.loadEntityWorkspace(projectId);
        const activeCard = snapshot.cards.find((card) => card.id === activeCardId);
        const nextRelationship = activeCard?.relationships.find((entry) => entry.id === relationship.id);

        set({
          capacity: snapshot.capacity,
          cards: snapshot.cards,
          characterTargetOptions: snapshot.characterTargetOptions,
          isRecoveringMedia: false,
          mediaRecoveryRequired: false,
          notice: null,
          raceSpeciesOptions: snapshot.raceSpeciesOptions,
          relationshipTargetOptions: snapshot.relationshipTargetOptions,
        });

        if (!nextRelationship) {
          throw new EntityActionError("not-found", "The saved relationship is unavailable.");
        }

        return nextRelationship;
      } catch (error) {
        set({
          notice: {
            kind: "error",
            message: formatEntityErrorMessage(error),
          },
        });
        throw error;
      }
    },

    deleteRelationship: async (relationshipId) => {
      const projectId = get().projectId;
      const activeCardId = get().activeCardId;

      if (!projectId || !activeCardId) {
        throw new EntityActionError("not-found", "That Entity Card is unavailable.");
      }

      try {
        const result = await deps.deleteEntityRelationship(relationshipId);
        const snapshot = await deps.loadEntityWorkspace(projectId);

        set({
          capacity: snapshot.capacity,
          cards: snapshot.cards,
          characterTargetOptions: snapshot.characterTargetOptions,
          isRecoveringMedia: false,
          mediaRecoveryRequired: false,
          notice: null,
          raceSpeciesOptions: snapshot.raceSpeciesOptions,
          relationshipTargetOptions: snapshot.relationshipTargetOptions,
        });

        return result;
      } catch (error) {
        set({
          notice: {
            kind: "error",
            message: formatEntityErrorMessage(error),
          },
        });
        throw error;
      }
    },

    clearNotice: () => {
      set({ notice: null });
    },

    clearLimitPrompt: () => {
      set({ limitPrompt: null });
    },

    markMediaRecoveryRequired: () => {
      set({ mediaRecoveryRequired: true });
    },

    refreshMediaUrls: async () => {
      const projectId = get().projectId;
      if (!projectId) {
        return;
      }

      set({
        isRecoveringMedia: true,
      });

      try {
        const snapshot = await deps.loadEntityWorkspace(projectId);

        set((state) => ({
          activeCardId: state.draftType
            ? null
            : resolveNextActiveCardId(
                snapshot.cards,
                state.activeCardId,
                state.activeCardId,
              ),
          capacity: snapshot.capacity,
          cards: snapshot.cards,
          characterTargetOptions: snapshot.characterTargetOptions,
          isRecoveringMedia: false,
          mediaRecoveryRequired: false,
          notice: null,
          raceSpeciesOptions: snapshot.raceSpeciesOptions,
          relationshipTargetOptions: snapshot.relationshipTargetOptions,
          unavailableCardId:
            state.unavailableCardId
            && !snapshot.cards.some((card) => card.id === state.unavailableCardId)
              ? state.unavailableCardId
              : null,
        }));
      } catch (error) {
        set({
          isRecoveringMedia: false,
          notice: {
            kind: "error",
            message: formatEntityErrorMessage(error),
          },
        });
      }
    },
  }));
}

export const entityWorkspaceStore = createEntityWorkspaceStore();

export function useEntityWorkspaceStore<T>(
  selector: (state: EntityWorkspaceStoreState) => T,
) {
  return useStore(entityWorkspaceStore, selector);
}

export function selectActiveEntityCard(
  state: Pick<EntityWorkspaceStoreState, "activeCardId" | "cards">,
) {
  return state.cards.find((card) => card.id === state.activeCardId) ?? null;
}

export function selectVisibleEntityCards(
  state: Pick<EntityWorkspaceStoreState, "cards" | "filters">,
) {
  return applyEntityFilters(state.cards, state.filters);
}

export function selectSelectedCardHiddenByFilters(
  state: Pick<EntityWorkspaceStoreState, "activeCardId" | "cards" | "draftType" | "filters">,
) {
  return getSelectedCardHiddenByFilters(
    applyEntityFilters(state.cards, state.filters),
    state.activeCardId,
    Boolean(state.draftType),
  );
}
